import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Search, Edit, Eye, CheckCircle, XCircle, Users, UserCheck, UserPlus, Filter, ArrowUpDown, ArrowUp, ArrowDown, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { firebaseService, Turma } from '../services/firebaseService';
import { User } from '../types/User';
import { useNotification } from '../hooks/useNotification';
import { usePermissions } from '../hooks/usePermissions';
import TurmaModal from '../components/TurmaModal';

const Turmas: React.FC = () => {
  const { userProfile } = useAuth();
  const { showSuccess, showError, showConfirm, showInfo, NotificationComponent } = useNotification();
  const { canCreate, canEdit, canDelete, canView } = usePermissions();
  const [turmas, setTurmas] = useState<Turma[]>([]);
  const [professores, setProfessores] = useState<User[]>([]);
  const [alunos, setAlunos] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('todos');
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState<'nome' | 'ocupacao' | 'status'>('nome');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [selectedTurma, setSelectedTurma] = useState<Turma | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showTurmaModal, setShowTurmaModal] = useState(false);
  const [showAddAlunoModal, setShowAddAlunoModal] = useState(false);
  const [showAddProfessorModal, setShowAddProfessorModal] = useState(false);

  // Carregar todos os dados do Firebase de forma otimizada
  useEffect(() => {
    const loadData = async () => {
      if (!userProfile?.empresaCnpj) return;

      try {
        setLoading(true);
        setError(null);
        
        // Carregar todos os dados de uma vez (otimizado)
        const { turmas: turmasData, professores: professoresData, alunos: alunosData } = await firebaseService.getAllDataWithAlunos(userProfile.empresaCnpj);
        
        setTurmas(turmasData);
        setProfessores(professoresData);
        setAlunos(alunosData);
        console.log('✅ Dados carregados:', {
          turmas: turmasData.length,
          professores: professoresData.length,
          alunos: alunosData.length
        });
      } catch (err) {
        console.error('❌ Erro ao carregar dados:', err);
        setError('Erro ao carregar dados');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userProfile?.empresaCnpj]);

  // Filtrar e ordenar turmas
  const filteredAndSortedTurmas = turmas
    .filter(turma => {
    const matchesSearch = (turma.nome || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'todos' || 
                         (statusFilter === 'ativo' && turma.ativo) ||
                         (statusFilter === 'inativo' && !turma.ativo);
    return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let aValue: string | number | boolean;
      let bValue: string | number | boolean;

      switch (sortBy) {
        case 'nome':
          aValue = (a.nome || '').toLowerCase();
          bValue = (b.nome || '').toLowerCase();
          break;
        case 'ocupacao':
          const aAlunos = getAlunosDaTurma(a.id).length;
          const aLimite = a.limiteAlunos || 0;
          const bAlunos = getAlunosDaTurma(b.id).length;
          const bLimite = b.limiteAlunos || 0;
          aValue = aLimite > 0 ? (aAlunos / aLimite) * 100 : 0;
          bValue = bLimite > 0 ? (bAlunos / bLimite) * 100 : 0;
          break;
        case 'status':
          aValue = a.ativo;
          bValue = b.ativo;
          break;
        default:
          aValue = (a.nome || '').toLowerCase();
          bValue = (b.nome || '').toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

  // Funções de ordenação
  const handleSort = (field: 'nome' | 'ocupacao' | 'status') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Função para obter professores da turma
  const getProfessoresDaTurma = (professoresIds: string[] | string | undefined): User[] => {
    if (!professoresIds) return [];
    
    // Se for uma string JSON, fazer parse
    let idsArray: string[] = [];
    if (typeof professoresIds === 'string') {
      try {
        idsArray = JSON.parse(professoresIds);
      } catch (e) {
        // Se não for JSON válido, tratar como array vazio
        console.warn('Erro ao fazer parse de professoresIds:', e);
        return [];
      }
    } else if (Array.isArray(professoresIds)) {
      idsArray = professoresIds;
    } else {
      return [];
    }
    
    if (idsArray.length === 0) return [];
    
    const professoresMap = new Map(professores.map(p => [p.id, p]));
    return idsArray
      .map(id => professoresMap.get(id))
      .filter((professor): professor is User => professor !== undefined);
  };

  // Função para obter alunos da turma (apenas alunos ativos)
  const getAlunosDaTurma = (turmaId: string): User[] => {
    if (!turmaId) return [];
    
    return alunos.filter(aluno => {
      // Filtrar apenas alunos ativos
      if (!aluno.bAtivo) return false;
      
      if (!aluno.turmas) return false;
      
      // Processar turmas - pode ser string JSON ou array
      let turmasArray: string[] = [];
      if (typeof aluno.turmas === 'string') {
        try {
          turmasArray = JSON.parse(aluno.turmas);
        } catch (e) {
          console.warn('Erro ao fazer parse de turmas do aluno:', e);
          return false;
        }
      } else if (Array.isArray(aluno.turmas)) {
        turmasArray = aluno.turmas;
      } else {
        return false;
      }
      
      return turmasArray.includes(turmaId);
    });
  };

  // Estatísticas
  const totalTurmas = turmas.length;
  const turmasAtivas = turmas.filter(t => t.ativo).length;
  const turmasInativas = totalTurmas - turmasAtivas;
  const totalAlunos = turmas.reduce((acc, turma) => acc + getAlunosDaTurma(turma.id).length, 0);

  // Função para formatar horário
  const formatHorario = (inicio: string, fim: string) => {
    if (!inicio || !fim) return 'Horário não definido';
    return `${inicio} - ${fim}`;
  };

  // Função para formatar dias da semana
  const formatDiasSemana = (dias: string[]) => {
    if (!Array.isArray(dias)) return 'Sem dias definidos';
    const diasMap: { [key: string]: string } = {
      'monday': 'Seg', 'tuesday': 'Ter', 'wednesday': 'Qua',
      'thursday': 'Qui', 'friday': 'Sex', 'saturday': 'Sáb', 'sunday': 'Dom'
    };
    return dias.map(dia => diasMap[dia] || dia).join(', ');
  };

  // Funções de modal
  const handleViewTurma = (turma: Turma) => {
    setSelectedTurma(turma);
    setShowModal(true);
  };

  const handleEditTurma = (turma: Turma) => {
    setSelectedTurma(turma);
    setShowTurmaModal(true);
  };

  const handleNewTurma = () => {
    setSelectedTurma(null);
    setShowTurmaModal(true);
  };

  const handleCloseTurmaModal = () => {
    setShowTurmaModal(false);
    setSelectedTurma(null);
  };

  const handleSaveTurma = async (turmaData: Partial<Turma>) => {
    if (!userProfile?.empresaCnpj) return;

    try {
      if (turmaData.id) {
        // Atualizar turma existente
        await firebaseService.updateTurma(userProfile.empresaCnpj, turmaData.id, turmaData);
      } else {
        // Criar nova turma
        await firebaseService.createTurma(userProfile.empresaCnpj, turmaData);
      }
      
      // Recarregar dados após criar ou atualizar
      const { turmas: turmasData } = await firebaseService.getAllDataWithAlunos(userProfile.empresaCnpj);
      setTurmas(turmasData);
      
      handleCloseTurmaModal();
      showSuccess(
        turmaData.id ? 'Turma atualizada' : 'Turma criada',
        turmaData.id ? 'Os dados da turma foram atualizados com sucesso.' : 'A turma foi cadastrada com sucesso.'
      );
      return Promise.resolve();
    } catch (error) {
      console.error('Erro ao salvar turma:', error);
      showError('Erro ao salvar', 'Não foi possível salvar a turma. Tente novamente.');
      throw error;
    }
  };

  const handleAddAluno = (turma: Turma) => {
    setSelectedTurma(turma);
    setShowAddAlunoModal(true);
  };

  const handleAddProfessor = (turma: Turma) => {
    setSelectedTurma(turma);
    setShowAddProfessorModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">{error}</div>
        <button onClick={() => window.location.reload()} className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors">
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <NotificationComponent />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <span className="text-gray-500 dark:text-gray-400">Turmas</span>
              </li>
              <li>
                <span className="text-gray-400">/</span>
              </li>
              <li>
                <span className="text-gray-900 dark:text-gray-100 font-medium">Todas</span>
              </li>
            </ol>
          </nav>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-2">Todas</h1>
        </div>
        {canCreate('turmas') && (
          <button 
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
            onClick={handleNewTurma}
          >
            <Plus className="h-4 w-4" />
          <span>Nova Turma</span>
          </button>
        )}
      </div>

      {/* Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 dark:border-gray-700 p-6">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <span className="font-medium">{filteredAndSortedTurmas.length}</span> turmas encontradas de{' '}
          <span className="font-medium">{totalTurmas}</span> turmas cadastradas
          {turmasAtivas > 0 && (
            <> • <span className="font-medium text-green-600">{turmasAtivas}</span> ativas</>
          )}
          {turmasInativas > 0 && (
            <> • <span className="font-medium text-orange-600">{turmasInativas}</span> inativas</>
          )}
          {totalAlunos > 0 && (
            <> • <span className="font-medium text-blue-600">{totalAlunos}</span> alunos</>
          )}
        </div>
      </div>

      {/* Search and Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Procurar por nome da turma"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 w-80"
            />
          </div>
          
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-2 px-4 py-2 border rounded-md transition-colors ${
              showFilters || statusFilter !== 'todos'
                ? 'border-green-500 bg-green-50 text-green-700 hover:bg-green-100'
                : 'border-primary-300 text-primary-600 hover:bg-primary-50'
            }`}
          >
            <Filter className="h-4 w-4" />
            <span>Filtros</span>
            {statusFilter !== 'todos' && (
              <span className="ml-1 h-2 w-2 rounded-full bg-green-600"></span>
            )}
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 dark:border-gray-700 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
          >
            <option value="todos">Todos os status</option>
            <option value="ativo">Apenas ativas</option>
            <option value="inativo">Apenas inativas</option>
          </select>
        </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setStatusFilter('todos');
                  setSearchTerm('');
                }}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 transition-colors"
              >
                Limpar Filtros
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('nome')}
                >
                  <div className="flex items-center gap-1">
                    Turma
                    {sortBy === 'nome' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 opacity-50" />
                    )}
                  </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Professores
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Alunos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Horário
                  </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('ocupacao')}
                >
                  <div className="flex items-center gap-1">
                    Ocupação
                    {sortBy === 'ocupacao' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 opacity-50" />
                    )}
                  </div>
                  </th>
                <th 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center gap-1">
                    Status
                    {sortBy === 'status' ? (
                      sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 opacity-50" />
                    )}
                  </div>
                  </th>
                <th className="relative px-6 py-3 w-40">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredAndSortedTurmas.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <Calendar className="h-12 w-12 text-gray-400 mb-3" />
                      <p className="text-gray-500 dark:text-gray-400 font-medium">Nenhuma turma encontrada</p>
                      <p className="text-sm text-gray-400 mt-1">
                        {searchTerm || statusFilter !== 'todos' 
                          ? 'Tente ajustar os filtros de busca.' 
                          : 'Comece adicionando uma nova turma.'
                        }
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredAndSortedTurmas.map((turma) => {
                  const turmaProfessores = getProfessoresDaTurma(turma.professoresIds);
                  const turmaAlunos = getAlunosDaTurma(turma.id);
                  const ocupacaoPercent = turma.limiteAlunos > 0 ? Math.round((turmaAlunos.length / turma.limiteAlunos) * 100) : 0;

                  return (
                    <tr key={turma.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{turma.nome || 'Sem nome'}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">{turma.tipoTurma || 'Tipo não definido'}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {turmaProfessores.map((professor) => (
                            <span key={professor.id} className="inline-flex items-center gap-x-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 border border-green-200">
                              <UserCheck className="h-3 w-3" />
                              {(professor.nome || '').split(' ')[0]}
                            </span>
                          ))}
                          {turmaProfessores.length === 0 && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">Sem professores</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-gray-100">
                          {turmaAlunos.length}/{turma.limiteAlunos || 0}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                        {formatHorario(turma.horarioInicio, turma.horarioFim)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="w-20 bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${
                                  ocupacaoPercent >= 100 ? 'bg-red-500' : 
                                  ocupacaoPercent >= 80 ? 'bg-yellow-500' : 'bg-green-500'
                                }`}
                                style={{ width: `${Math.min(ocupacaoPercent, 100)}%` }}
                              ></div>
                            </div>
                          <span className="text-sm text-gray-900 dark:text-gray-100 min-w-[3rem]">{ocupacaoPercent}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          turma.ativo 
                            ? 'bg-green-100 text-green-800 border border-green-200' 
                            : 'bg-red-100 text-red-800 border border-red-200'
                        }`}>
                          {turma.ativo ? 'Ativa' : 'Inativa'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-x-1">
                          <button 
                            onClick={() => handleViewTurma(turma)}
                            className="text-primary-600 hover:text-primary-900 p-2 rounded hover:bg-primary-50 transition-colors"
                            title="Ver detalhes"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleAddAluno(turma)}
                            className="text-green-600 hover:text-green-900 p-2 rounded hover:bg-green-50 transition-colors"
                            title="Adicionar aluno"
                          >
                            <UserPlus className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleAddProfessor(turma)}
                            className="text-purple-600 hover:text-purple-900 p-2 rounded hover:bg-purple-50 transition-colors"
                            title="Adicionar professor"
                          >
                            <UserCheck className="h-4 w-4" />
                          </button>
                          {canEdit('turmas') && (
                            <button 
                              onClick={() => handleEditTurma(turma)}
                              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:text-gray-100 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                              title="Editar"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
              </tbody>
            </table>
          </div>
        </div>

      {/* Modal de Detalhes da Turma */}
      {showModal && selectedTurma && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Calendar className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Detalhes da Turma</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">ID: {selectedTurma.id}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
              </div>
              
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nome</label>
                    <p className="text-gray-900 dark:text-gray-100">{selectedTurma.nome || 'Sem nome'}</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo</label>
                    <p className="text-gray-900 dark:text-gray-100">{selectedTurma.tipoTurma || 'Tipo não definido'}</p>
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Horário</label>
                    <p className="text-gray-900 dark:text-gray-100">{formatHorario(selectedTurma.horarioInicio, selectedTurma.horarioFim)}</p>
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Professores ({getProfessoresDaTurma(selectedTurma.professoresIds).length})</label>
                    <div className="flex flex-wrap gap-2">
                      {getProfessoresDaTurma(selectedTurma.professoresIds).map((professor) => (
                        <span key={professor.id} className="inline-flex items-center gap-x-1 px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 border border-green-200">
                          <UserCheck className="h-3 w-3" />
                          {professor.nome || 'Sem nome'}
                        </span>
                      ))}
                      {getProfessoresDaTurma(selectedTurma.professoresIds).length === 0 && (
                        <span className="text-sm text-gray-500 dark:text-gray-400">Nenhum professor vinculado</span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Dias da Semana</label>
                    <p className="text-gray-900 dark:text-gray-100">
                    {Array.isArray(selectedTurma.diasSemana) && selectedTurma.diasSemana.length > 0 
                      ? formatDiasSemana(selectedTurma.diasSemana)
                      : 'Sem dias definidos'
                    }
                  </p>
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Capacidade</label>
                    <p className="text-gray-900 dark:text-gray-100">
                    {getAlunosDaTurma(selectedTurma.id).length} / {selectedTurma.limiteAlunos || 0} alunos
                  </p>
                </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Alunos ({getAlunosDaTurma(selectedTurma.id).length})</label>
                    <div className="flex flex-wrap gap-2">
                      {getAlunosDaTurma(selectedTurma.id).map((aluno) => (
                        <span key={aluno.id} className="inline-flex items-center gap-x-1 px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 border border-blue-200">
                          <Users className="h-3 w-3" />
                          {aluno.nome || 'Sem nome'}
                        </span>
                      ))}
                      {getAlunosDaTurma(selectedTurma.id).length === 0 && (
                        <span className="text-sm text-gray-500 dark:text-gray-400">Nenhum aluno vinculado</span>
                      )}
                    </div>
                  </div>
                
                <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                    <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${
                    selectedTurma.ativo 
                        ? 'bg-green-100 text-green-800 border border-green-200' 
                        : 'bg-red-100 text-red-800 border border-red-200'
                  }`}>
                    {selectedTurma.ativo ? 'Ativa' : 'Inativa'}
                  </span>
                  </div>
                  
                  {selectedTurma.observacoes && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Observações</label>
                      <p className="text-gray-900 dark:text-gray-100">{selectedTurma.observacoes}</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex justify-end gap-x-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button 
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Fechar
                </button>
                <button 
                  onClick={() => {
                    setShowModal(false);
                    handleEditTurma(selectedTurma);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Editar Turma
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Adicionar Aluno */}
      {showAddAlunoModal && selectedTurma && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                    <UserPlus className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Adicionar Aluno à Turma</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{selectedTurma.nome || 'Sem nome'}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowAddAlunoModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
              </div>
              
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Selecionar Aluno</label>
                  <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm">
                    <option value="">Escolha um aluno...</option>
                    {alunos.filter(aluno => {
                      // Filtrar apenas alunos ativos
                      if (!aluno.bAtivo) return false;
                      
                      const turmaAlunos = getAlunosDaTurma(selectedTurma.id);
                      const turmaAlunosIds = turmaAlunos.map(a => a.id);
                      return !turmaAlunosIds.includes(aluno.id);
                    }).map(aluno => (
                      <option key={aluno.id} value={aluno.id}>
                        {aluno.nome || 'Sem nome'} - {aluno.email || 'Sem email'}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end gap-x-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button 
                  onClick={() => setShowAddAlunoModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button 
                  onClick={() => {
                    // TODO: Implementar lógica de adicionar aluno
                    showInfo('Em desenvolvimento', 'A funcionalidade de adicionar aluno será implementada em breve.');
                    setShowAddAlunoModal(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Adicionar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Adicionar Professor */}
      {showAddProfessorModal && selectedTurma && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <UserCheck className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Adicionar Professor à Turma</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{selectedTurma.nome || 'Sem nome'}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowAddProfessorModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
              </div>
              
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Selecionar Professor</label>
                  <select className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm">
                    <option value="">Escolha um professor...</option>
                    {professores.filter(professor => {
                      const turmaProfIds = getProfessoresDaTurma(selectedTurma.professoresIds).map(p => p.id);
                      return !turmaProfIds.includes(professor.id);
                    }).map(professor => (
                      <option key={professor.id} value={professor.id}>
                        {professor.nome || 'Sem nome'} - {professor.email || 'Sem email'}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="flex justify-end gap-x-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button 
                  onClick={() => setShowAddProfessorModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button 
                  onClick={() => {
                    // TODO: Implementar lógica de adicionar professor
                    showInfo('Em desenvolvimento', 'A funcionalidade de adicionar professor será implementada em breve.');
                    setShowAddProfessorModal(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
                >
                  Adicionar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição/Criação de Turma */}
      <TurmaModal
        turma={selectedTurma}
        isOpen={showTurmaModal}
        onClose={handleCloseTurmaModal}
        onSave={handleSaveTurma}
        professores={professores}
      />
    </div>
  );
};

export default Turmas;